// =====================================================
// 白板系统 - 布局组件 (KeepAlive 版)
// 左侧可折叠侧边栏 + 标签栏 + KeepAlive 编辑器容器
// =====================================================

import { Outlet, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, LayoutGrid } from 'lucide-react';
import { ProjectSidebar } from './components/ProjectSidebar';
import { WhiteboardTabBar } from './components/WhiteboardTabBar';
import { KeepAliveEditorContainer } from './components/KeepAliveEditorContainer';
import { useWhiteboardTabsStore } from './stores/whiteboard-tabs-store';
import { useWhiteboardStore } from './stores/whiteboard-store';
import { useEffect } from 'react';

const navItems = [
  { path: '/whiteboard', label: '所有白板', icon: LayoutGrid, exact: true },
];

export function WhiteboardLayout() {
  const location = useLocation();
  const isEditorRoute = location.pathname.match(/^\/whiteboard\/[^/]+$/);
  const editorId = isEditorRoute ? location.pathname.replace('/whiteboard/', '') : null;

  const { tabs, openTab } = useWhiteboardTabsStore();
  const projects = useWhiteboardStore((s) => s.projects);
  const collabProjects = useWhiteboardStore((s) => s.collabProjects);

  // 当通过 URL 导航到编辑器页面时，自动打开标签页
  useEffect(() => {
    if (!editorId) return;

    // 从已加载的项目列表中查找名称
    const allProjects = [...projects, ...collabProjects];
    const project = allProjects.find((p) => p.id === editorId);
    const name = project?.name || '加载中...';

    openTab(editorId, name);
  }, [editorId, projects, collabProjects, openTab]);

  const hasOpenTabs = tabs.length > 0;
  // 顶部导航栏：仅在没有打开任何标签页的白板列表页显示
  const showTopNav = !isEditorRoute && !hasOpenTabs;

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* 顶部导航栏（仅在没有标签页的列表页显示完整导航） */}
      {showTopNav && (
        <nav className="bg-white border-b border-gray-200 px-4 py-2 shrink-0 flex items-center">
          {/* macOS 红绿灯与 Knowledge Hub 标题避让区 */}
          <div
            className="shrink-0 w-[290px]"
            style={{ WebkitAppRegion: 'drag' } as React.CSSProperties}
          />
          <div className="max-w-7xl flex-1 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/uadmin"
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                返回管理后台
              </Link>

              <div className="h-4 w-px bg-gray-300" />

              <div className="flex items-center gap-1">
                {navItems.map((item) => {
                  const isActive = item.exact
                    ? location.pathname === item.path
                    : location.pathname.startsWith(item.path);
                  const Icon = item.icon;

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </nav>
      )}

      {/* 标签栏（有打开的标签页时显示） */}
      {hasOpenTabs && <WhiteboardTabBar />}

      {/* 主体区域 */}
      <div className="flex-1 flex overflow-hidden">
        {/* 编辑器模式：显示侧边栏 */}
        {isEditorRoute && <ProjectSidebar />}

        {/* 内容区 */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* 列表页内容（通过 Outlet 渲染） */}
          {!isEditorRoute && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <Outlet />
            </div>
          )}

          {/* KeepAlive 编辑器容器：只要有标签页就保留挂载，在列表页时隐藏而非销毁 */}
          {hasOpenTabs && (
            <div
              style={{
                display: isEditorRoute ? 'flex' : 'none',
                flex: 1,
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
              }}
            >
              <KeepAliveEditorContainer />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default WhiteboardLayout;
